import { useListPartners } from '@/api/accounting-dealer'
import { Box, Button, Divider, Stack, Tooltip, Typography } from '@mui/material'
import { useMemo } from 'react'
import { generatePath, useNavigate, useOutletContext } from 'react-router-dom'
import { PartnerDocuments } from './partner-documents'
import { CompanyDocuments } from './company-documents'
import { LoadingButton } from '@mui/lab'
import { routes } from '@/router'
import { useCreateContract } from '@/api/accounting-dealer/contract/create-contract'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { DocumentsSchemaType, getDocumentSchema } from './schema'
import { ReadDealResponse } from '@/types'

export const DocumentsPage = () => {
  const { deal } = useOutletContext<ReadDealResponse>()
  const navigate = useNavigate()

  const { data: partners } = useListPartners({ companyKey: deal.company.key })

  const partnerships = useMemo(
    () => partners?.filter(({ is_main }) => !is_main) || [],
    [partners],
  )

  const mainPartnership = useMemo(
    () => partners?.find(({ is_main }) => is_main),
    [partners],
  )

  const form = useForm<DocumentsSchemaType>({
    resolver: zodResolver(getDocumentSchema(deal.company.status)),
  })

  const { mutateAsync: createContract, isPending: isCreatingContract } =
    useCreateContract({ deal_key: deal.key })

  const handleContinueButton = async () => {
    const { code } = await createContract()
    if (code === 'contract_already_signed') {
      navigate(routes.dashboard)
      return
    }
    navigate(generatePath(routes.hiring.contract, { deal_key: deal.key }))
  }

  return (
    <Stack spacing={4}>
      {mainPartnership ? (
        <Stack spacing={2}>
          <Typography variant="h3" pb={2}>
            Documentos do Sócio Principal ({mainPartnership.name})
          </Typography>
          <FormProvider {...form}>
            <PartnerDocuments
              isMainPartner
              partnerKey={mainPartnership?.key || ''}
            />
          </FormProvider>
        </Stack>
      ) : null}

      {partnerships.length > 0 ? (
        <>
          <Divider />
          <Stack spacing={2}>
            {partnerships.map(({ name, key }, idx) => (
              <Stack spacing={2} key={idx}>
                <Typography variant="h3" pb={2}>
                  Documentos do Sócio ({name})
                </Typography>
                <FormProvider {...form}>
                  <PartnerDocuments partnerKey={key} partnerIndex={idx} />
                </FormProvider>
              </Stack>
            ))}
          </Stack>
        </>
      ) : null}

      <Divider />

      <Stack spacing={2}>
        <Typography variant="h3" pb={2}>
          Documentos da Empresa
        </Typography>
        <FormProvider {...form}>
          <CompanyDocuments entity_key={deal.company.key} />
        </FormProvider>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={8}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={() =>
            navigate(
              generatePath(routes.hiring.company, { deal_key: deal.key }),
            )
          }
        >
          Voltar
        </Button>
        <Tooltip
          arrow
          placement="left"
          title="Adicione todos os documentos para continuar"
          disableHoverListener={form.formState.isValid}
          disableTouchListener={form.formState.isValid}
          enterTouchDelay={0}
          leaveTouchDelay={1000}
        >
          <Box
            sx={{
              cursor: !form.formState.isValid ? 'not-allowed' : 'pointer',
            }}
          >
            <LoadingButton
              type="submit"
              onClick={handleContinueButton}
              size="large"
              variant="contained"
              loading={isCreatingContract}
              disabled={!form.formState.isValid}
            >
              Avançar
            </LoadingButton>
          </Box>
        </Tooltip>
      </Stack>
    </Stack>
  )
}
